import { useEffect, useState } from 'react';
import { now } from '../lib/time';

/**
 * Days to Khao Lak, on the landing screen.
 *
 * **Midnight on the 5th, Khao Lak time — not the welcome dinner at 17:30.**
 * The two are a working day apart and the difference matters: this is asked to
 * disappear *on* the 5th, and picking midnight means the number reaching zero
 * and the component vanishing are the same event. Targeting the dinner would
 * leave it counting down through the morning of a day it was supposed to be
 * gone from, and targeting midnight while hiding at some other moment would
 * mean it either vanished mid-count or sat at 0 for hours.
 *
 * Fixed offset `+07:00` rather than a timezone conversion, because Bangkok has
 * no DST — the offset never moves, which is the same reason `time.ts` can get
 * away with formatting everything through one locale.
 */
const TARGET = Date.parse('2026-11-05T00:00:00+07:00');

/**
 * Always four units, always ticking once a second.
 *
 * An earlier version dropped the seconds while the wedding was still weeks out,
 * on the reasoning that nobody watches a digit that far from the event. It read
 * as broken — three numbers sitting perfectly still look like a stopped clock,
 * not like a deliberate choice, and the moving second is most of what makes a
 * countdown feel alive.
 *
 * It also carried a real bug: seconds appeared once the days reached zero, but
 * the fast tick only began in the final hour, so for twenty-three hours it
 * would have shown a seconds counter advancing once a minute. Ticking always
 * removes the threshold, and with it the whole class of mistake.
 *
 * The cost is one re-render a second of four numbers. That is nothing.
 */
const TICK_MS = 1000;

function split(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return {
    days: Math.floor(total / 86400),
    hours: Math.floor((total % 86400) / 3600),
    minutes: Math.floor((total % 3600) / 60),
    seconds: total % 60,
  };
}

export default function Countdown() {
  // `now()`, never `Date.now()`, so `?at=2026-11-05T09:00` previews the
  // vanishing without waiting for November.
  const [at, setAt] = useState(now);
  const remaining = TARGET - at;

  useEffect(() => {
    if (remaining <= 0) return undefined;

    // Fire on the second boundary rather than a second from whenever this
    // mounted, otherwise the digit turns over at an arbitrary offset and two
    // phones side by side disagree about what second it is.
    const delay = TICK_MS - (Date.now() % TICK_MS);
    const timer = setTimeout(() => setAt(now()), delay);
    return () => clearTimeout(timer);
  }, [at, remaining]);

  // The whole point of the ask: gone on the day, not a "0 days" card.
  if (remaining <= 0) return null;

  const { days, hours, minutes, seconds } = split(remaining);

  // Four units, unconditionally. `00 days` on the last day is honest and keeps
  // the row from reflowing as units drop out — and there is no branch here to
  // fall out of step with the tick.
  const units = [
    [days, 'days'], [hours, 'hours'], [minutes, 'min'], [seconds, 'sec'],
  ] as const;

  return (
    <div className="countdown">
      {/* One quiet sentence for a screen reader, at day resolution. The digits
          below are hidden from it: a polite live region that re-reads itself
          every second is unusable, and the exact second is not the content. */}
      <p className="sr-only">
        {days >= 1
          ? `${days} ${days === 1 ? 'day' : 'days'} until the wedding week begins.`
          : 'The wedding week begins today.'}
      </p>

      <div className="countdown-units" aria-hidden="true">
        {units.map(([value, label]) => (
          <div key={label} className="countdown-unit">
            <span className="flip">
              {/* Two tiles per unit, one per digit, because a split-flap board
                  has one flap per character — a single tile holding "07" flips
                  the 0 that did not change. */}
              {String(value).padStart(2, '0').split('').map((digit, i) => (
                <span key={i} className="flip-tile">
                  {/*
                    `key={digit}` is doing real work: React remounts the element
                    when the character changes, which restarts the CSS
                    animation. Without it the class is already applied and the
                    animation never replays, so the board would sit still.
                    It also means an unchanged digit does not animate, which is
                    the whole behaviour of a split-flap.
                  */}
                  <span key={digit} className="flip-face">{digit}</span>
                  <span className="flip-seam" />
                </span>
              ))}
            </span>
            <span className="countdown-label">{label}</span>
          </div>
        ))}
      </div>

      <p className="countdown-caption" aria-hidden="true">until the wedding week begins</p>
    </div>
  );
}
